/**
 * Dependency-free HTML sanitizer that runs identically on the server (edge
 * runtime, no DOM) and in the browser. Replaces isomorphic-dompurify, which
 * throws in a DOM-less worker environment.
 *
 * Strategy: tokenize the input, drop any tag not on the allowlist (and the
 * entire contents of dangerous containers like <script>/<style>), and strip any
 * attribute not on the allowlist or whose URL/value looks unsafe.
 */

const VOID_TAGS = new Set([
  "br", "hr", "img", "source", "col", "input", "wbr", "area", "base", "embed", "link", "meta", "param", "track",
]);

// Tags whose entire content is discarded when encountered.
const DROP_CONTENT_TAGS = new Set([
  "script", "style", "iframe", "object", "embed", "noscript", "template", "svg", "math", "form", "frame", "frameset",
]);

const URL_ATTRS = new Set(["href", "src", "srcset", "xlink:href", "action", "formaction"]);

// Tags that implicitly close a still-open sibling of the same kind.
const IMPLIED_CLOSE: Record<string, string[]> = {
  li: ["li"],
  dt: ["dt", "dd"],
  dd: ["dt", "dd"],
  p: ["p"],
  tr: ["tr", "td", "th"],
  td: ["td", "th"],
  th: ["td", "th"],
};

export interface SanitizeOptions {
  allowedTags: string[];
  allowedAttrs: string[];
  allowedUriRegexp?: RegExp;
}

const DEFAULT_URI_REGEXP = /^(?:(?:https?|mailto|tel):|[/#?]|[^a-z]|[a-z+.-]+(?:[^a-z+.\-:]|$))/i;

function isSafeUrl(value: string, re: RegExp): boolean {
  const trimmed = value.replace(/[\u0000-\u0020\u00a0]+/g, "").toLowerCase();
  if (/^(javascript|vbscript|data):/i.test(trimmed)) {
    // Allow inline images only.
    if (!/^data:image\/(png|jpe?g|gif|webp|avif|svg\+xml);/i.test(trimmed)) return false;
  }
  return re.test(value.trim());
}

function sanitizeSrcset(value: string, re: RegExp): string | null {
  const parts = value
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);
  const kept: string[] = [];
  for (const part of parts) {
    const url = part.split(/\s+/)[0] ?? "";
    if (url && isSafeUrl(url, re)) kept.push(part);
  }
  return kept.length ? kept.join(", ") : null;
}

function sanitizeStyle(value: string): string | null {
  // Reject anything that can fetch or execute.
  if (/(expression|javascript:|vbscript:|url\s*\(|@import|behaviou?r\s*:)/i.test(value)) return null;
  const cleaned = value.replace(/[<>]/g, "").trim();
  return cleaned || null;
}

function escapeAttr(value: string): string {
  return value.replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

const ATTR_RE = /([a-zA-Z_:][-a-zA-Z0-9_:.]*)(?:\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s"'`=<>]+)))?/g;

function sanitizeAttributes(raw: string, allowedAttrs: Set<string>, re: RegExp): string {
  let out = "";
  ATTR_RE.lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = ATTR_RE.exec(raw)) !== null) {
    const name = match[1].toLowerCase();
    let value = match[2] ?? match[3] ?? match[4] ?? "";
    if (!allowedAttrs.has(name)) continue;
    if (name.startsWith("on")) continue;

    if (name === "srcset") {
      const safe = sanitizeSrcset(value, re);
      if (!safe) continue;
      value = safe;
    } else if (URL_ATTRS.has(name)) {
      if (!isSafeUrl(value, re)) continue;
    } else if (name === "style") {
      const safe = sanitizeStyle(value);
      if (!safe) continue;
      value = safe;
    }

    out += ` ${name}="${escapeAttr(value)}"`;
  }
  return out;
}

export function sanitizeHtml(input: string, options: SanitizeOptions): string {
  if (!input) return "";
  const allowedTags = new Set(options.allowedTags.map((t) => t.toLowerCase()));
  const allowedAttrs = new Set(options.allowedAttrs.map((a) => a.toLowerCase()));
  const uriRe = options.allowedUriRegexp ?? DEFAULT_URI_REGEXP;

  let out = "";
  let i = 0;
  const openStack: string[] = [];
  let skipUntil: string | null = null;

  while (i < input.length) {
    const lt = input.indexOf("<", i);
    if (lt === -1) {
      if (!skipUntil) out += input.slice(i);
      break;
    }
    if (!skipUntil) out += input.slice(i, lt);

    // A "<" not starting a tag is literal text — escape it.
    if (!/^<[a-zA-Z/!?]/.test(input.slice(lt, lt + 2))) {
      if (!skipUntil) out += "&lt;";
      i = lt + 1;
      continue;
    }

    // Comments and doctype/CDATA — drop entirely.
    if (input.startsWith("<!--", lt)) {
      const end = input.indexOf("-->", lt + 4);
      i = end === -1 ? input.length : end + 3;
      continue;
    }
    if (input.startsWith("<!", lt) || input.startsWith("<?", lt)) {
      const end = input.indexOf(">", lt);
      i = end === -1 ? input.length : end + 1;
      continue;
    }

    const gt = input.indexOf(">", lt);
    if (gt === -1) {
      // Stray "<" — escape it and stop.
      if (!skipUntil) out += "&lt;";
      i = lt + 1;
      continue;
    }

    const rawTag = input.slice(lt + 1, gt);
    const isClosing = rawTag.startsWith("/");
    const body = isClosing ? rawTag.slice(1) : rawTag;
    const nameMatch = /^([a-zA-Z][a-zA-Z0-9:-]*)/.exec(body.trim());
    if (!nameMatch) {
      i = gt + 1;
      continue;
    }
    const name = nameMatch[1].toLowerCase();
    const selfClosing = rawTag.trimEnd().endsWith("/");

    if (skipUntil) {
      if (isClosing && name === skipUntil) skipUntil = null;
      i = gt + 1;
      continue;
    }

    if (DROP_CONTENT_TAGS.has(name)) {
      if (!isClosing && !selfClosing && !VOID_TAGS.has(name)) skipUntil = name;
      i = gt + 1;
      continue;
    }

    if (!allowedTags.has(name)) {
      // Unknown tag: drop the tag but keep its inner text.
      i = gt + 1;
      continue;
    }

    if (isClosing) {
      const idx = openStack.lastIndexOf(name);
      if (idx !== -1) {
        // Close any tags left open inside it.
        for (let k = openStack.length - 1; k >= idx; k--) out += `</${openStack[k]}>`;
        openStack.length = idx;
      }
      i = gt + 1;
      continue;
    }

    // Auto-close an open sibling (e.g. `<li>a<li>b`) so nesting stays valid.
    const implied = IMPLIED_CLOSE[name];
    if (implied) {
      const last = openStack[openStack.length - 1];
      if (last && implied.includes(last)) {
        out += `</${last}>`;
        openStack.pop();
      }
    }

    const attrs = sanitizeAttributes(body.slice(nameMatch[1].length).replace(/\/\s*$/, ""), allowedAttrs, uriRe);
    if (VOID_TAGS.has(name)) {
      out += `<${name}${attrs} />`;
    } else if (selfClosing) {
      out += `<${name}${attrs}></${name}>`;
    } else {
      out += `<${name}${attrs}>`;
      openStack.push(name);
    }
    i = gt + 1;
  }

  for (let k = openStack.length - 1; k >= 0; k--) out += `</${openStack[k]}>`;
  return out;
}
